import { useState } from "react";
import { Loader2, Mail, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { api, apiError, fmtDate, humanize } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { Field, Panel, QueryState, StatusBadge } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const ROLES = ["admin", "approver", "editor", "viewer"];
const ROLE_HINT = { admin: "Edit, approve, manage settings", approver: "Review and approve", editor: "Create and edit drafts", viewer: "Read only" };

function InviteForm({ onDone }) {
  const { path } = useWorkspace();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(path("/invitations"), { email, role });
      toast[data.email_sent ? "success" : "warning"](data.email_sent ? `Invitation emailed to ${email}` : "Invitation created, but the email could not be delivered");
      setEmail(""); onDone();
    } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 sm:items-end" data-testid="invite-form">
      <div className="flex-1"><Field label="Invite by email"><input data-testid="invite-email-input" type="email" required className="cq-input" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} /></Field></div>
      <div className="sm:w-48"><Field label="Role"><select data-testid="invite-role-select" className="cq-input" value={role} onChange={(e) => setRole(e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}</select></Field></div>
      <button data-testid="invite-submit-button" disabled={busy} className="cq-btn cq-btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Send invite</button>
    </form>
  );
}

function Invitations() {
  const { path, refresh } = useWorkspace();
  const q = useWsQuery("/invitations");
  const [toRevoke, setToRevoke] = useState(null);
  const revoke = async () => { try { await api.delete(path(`/invitations/${toRevoke.id}`)); toast.success("Invitation revoked"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const resend = async (i) => {
    try { const { data } = await api.post(path("/invitations"), { email: i.email, role: i.role }); toast[data.email_sent ? "success" : "warning"](data.email_sent ? "New invitation emailed; the old link no longer works" : "Invitation renewed, but the email could not be delivered. Try again shortly."); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const open = (q.data || []).filter((i) => ["pending", "expired"].includes(i.status));
  if (!open.length) return null;
  return (
    <div className="mt-5" data-testid="invitation-list"><div className="cq-muted-label mb-2">Invitations</div>
      <div className="divide-y divide-[#172342] rounded-lg border border-[#172342]">{open.map((i) => (
        <div key={i.id} data-testid={`invitation-row-${i.id}`} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
          <Mail size={14} className="text-[#7C8BAD]" /><span className="text-[#F7FAFF] flex-1 min-w-0 truncate">{i.email}</span>
          <span className="text-xs text-[#AAB6CC]">{humanize(i.role)}</span><StatusBadge status={i.status} />
          <span className="text-xs text-[#7C8BAD]">Expires {fmtDate(i.expires_at)}</span>
          {i.status === "pending" && !i.email_sent && <span className="text-[11px] text-[#F4B860]">Email not delivered</span>}
          <button data-testid={`invitation-resend-${i.id}`} onClick={() => resend(i)} className="cq-btn cq-btn-ghost !py-1 !text-xs">Resend</button>
          {i.status === "pending" && <button data-testid={`invitation-revoke-${i.id}`} onClick={() => setToRevoke(i)} className="cq-btn cq-btn-ghost !p-1.5" title="Revoke"><X size={13} /></button>}
        </div>))}</div>
      <ConfirmDialog open={!!toRevoke} onOpenChange={(o) => !o && setToRevoke(null)} danger title={`Revoke the invitation for ${toRevoke?.email}?`} description="The link stops working immediately. This is logged." confirmLabel="Revoke" onConfirm={revoke} />
    </div>
  );
}

function MemberRow({ m, manage, onRemove }) {
  const { path, refresh } = useWorkspace();
  const { user } = useAuth();
  const editable = manage && m.role !== "owner" && m.user_id !== user?.user_id;
  const change = async (role) => { try { await api.patch(path(`/members/${m.id}`), { role }); toast.success("Role updated"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div data-testid={`member-row-${m.id}`} className="py-3 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-0"><div className="text-sm text-[#F7FAFF]">{m.user?.name}{m.user_id === user?.user_id && <span className="text-xs text-[#7C8BAD]"> (you)</span>}</div><div className="text-xs text-[#7C8BAD] truncate">{m.user?.email}{m.briefing_email && <span data-testid={`member-briefing-${m.id}`} className="ml-2 text-[#22E6F2]">· Monday briefing email</span>}</div></div>
      {editable ? (<>
        <select data-testid={`member-role-select-${m.id}`} value={m.role} onChange={(e) => change(e.target.value)} className="cq-input !w-36 !py-1.5 text-xs">{ROLES.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}</select>
        <button data-testid={`member-remove-${m.id}`} onClick={() => onRemove(m)} className="cq-btn cq-btn-ghost !p-1.5" title="Remove"><Trash2 size={13} /></button>
      </>) : <span className="cq-chip text-[#22E6F2] border-[#22E6F2]/30 bg-[#22E6F2]/10">{humanize(m.role)}</span>}
    </div>
  );
}

export default function TeamPanel() {
  const { path, refresh, can } = useWorkspace();
  const q = useWsQuery("/members");
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api.get("/roles").then((r) => r.data) });
  const [remove, setRemove] = useState(null);
  const manage = can("manage_members");
  const doRemove = async () => { try { await api.delete(path(`/members/${remove.id}`)); toast.success("Member removed"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Panel title="Team & roles" eyebrow="Access" testId="settings-members">
      {manage && <InviteForm onDone={refresh} />}
      {manage && <Invitations />}
      <div className={manage ? "mt-6" : ""}><div className="cq-muted-label mb-1">Members</div>
        <QueryState q={q}>{(ms) => <div className="divide-y divide-[#172342]">{ms.map((m) => <MemberRow key={m.id} m={m} manage={manage} onRemove={setRemove} />)}</div>}</QueryState>
      </div>
      {roles.data && (
        <div className="mt-6 overflow-x-auto" data-testid="settings-permission-matrix">
          <div className="cq-muted-label mb-2">Permission model</div>
          <table className="w-full text-xs"><thead><tr><th className="text-left py-2 text-[#7C8BAD] font-medium">Role</th>{roles.data.permissions.map((p) => <th key={p} className="py-2 px-2 text-[#7C8BAD] font-medium">{humanize(p)}</th>)}</tr></thead>
            <tbody>{Object.entries(roles.data.roles).map(([r, ps]) => <tr key={r} className="border-t border-[#172342]"><td className="py-2 text-[#F7FAFF]">{humanize(r)}<div className="text-[10.5px] text-[#7C8BAD]">{ROLE_HINT[r] || "Full control"}</div></td>{roles.data.permissions.map((p) => <td key={p} className="text-center">{ps.includes(p) ? <span className="text-[#4BE3A4]">●</span> : <span className="text-[#243153]">○</span>}</td>)}</tr>)}</tbody>
          </table>
          <p className="mt-3 text-xs text-[#7C8BAD]">Permissions are enforced on the server for every request. Invitations expire after 7 days and only work for the invited email address.</p>
        </div>)}
      <ConfirmDialog open={!!remove} onOpenChange={(o) => !o && setRemove(null)} danger title={`Remove ${remove?.user?.name}?`} description="They lose access to this workspace immediately. This is recorded in the audit log." confirmLabel="Remove member" onConfirm={doRemove} />
    </Panel>
  );
}
